import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import typeOf from 'type-of';
import { Node, Value, Annotation, Point, PathUtils } from 'slate';
import { List, Map, Record } from 'immutable';

var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();







var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};



































var toConsumableArray = function (arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  } else {
    return Array.from(arr);
  }
};

/**
 * String.
 *
 * @type {String}
 */

var String = new Record({
  object: 'string',
  text: ''
});

/**
 * A rule to (de)serialize text nodes. This is automatically added to the HTML
 * serializer so that users don't have to worry about text-level serialization.
 *
 * @type {Object}
 */

var TEXT_RULE = {
  deserialize: function deserialize(el) {
    if (el.tagName && el.tagName.toLowerCase() === 'br') {
      return {
        object: 'text',
        text: '\n',
        marks: []
      };
    }

    if (el.nodeName === '#text') {
      if (el.nodeValue && el.nodeValue.match(/<!--.*?-->/)) return;

      return {
        object: 'text',
        text: el.nodeValue,
        marks: []
      };
    }
  },
  serialize: function serialize(obj, children) {
    if (obj.object === 'string') {
      return children.split('\n').reduce(function (array, text, i) {
        if (i !== 0) array.push(React.createElement('br', { key: i }));
        array.push(text);
        return array;
      }, []);
    }
  }
};

/**
 * A default `parseHtml` function that returns the `<body>` using `DOMParser`.
 *
 * @param {String} html
 * @return {Object}
 */

function defaultParseHtml(html) {
  if (typeof DOMParser == 'undefined') {
    throw new Error('The native `DOMParser` global which the `Html` serializer uses by default is not present in this environment. You must supply the `options.parseHtml` function instead.');
  }

  var parsed = new DOMParser().parseFromString(html, 'text/html');
  var body = parsed.body;
  // COMPAT: in IE 11 body is null if html is an empty string

  return body || window.document.createElement('body');
}

/**
 * HTML serializer.
 *
 * @type {Html}
 */

var Html = function () {
  /**
   * Create a new serializer with `rules`.
   *
   * @param {Object} options
   *   @property {Array} rules
   *   @property {String|Object|Block} defaultBlock
   *   @property {Function} parseHtml
   */

  function Html() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    classCallCheck(this, Html);

    _initialiseProps.call(this);

    var _options$defaultBlock = options.defaultBlock,
        defaultBlock = _options$defaultBlock === undefined ? 'paragraph' : _options$defaultBlock,
        _options$parseHtml = options.parseHtml,
        parseHtml = _options$parseHtml === undefined ? defaultParseHtml : _options$parseHtml,
        _options$rules = options.rules,
        rules = _options$rules === undefined ? [] : _options$rules;


    defaultBlock = Node.createProperties(defaultBlock);

    this.rules = [].concat(toConsumableArray(rules), [TEXT_RULE]);
    this.defaultBlock = defaultBlock;
    this.parseHtml = parseHtml;
    this.annotations = Map();
    this.nodeToAnnotationsMap = Map();
  }

  /**
   * Deserialize pasted HTML.
   *
   * @param {String} html
   * @param {Object} options
   *   @property {Boolean} toRaw
   * @return {Value}
   */

  createClass(Html, [{
    key: 'updateAnnotationPaths',


    /**
     * Updates annotations found during deserialization to point to
     * paths in the document.
     *
     * @param {List} nodes
     * @param {List} path
     * @param {Object} annotations
     * @return {Object}
     */

    value: function updateAnnotationPaths(nodes, path, annotations) {
      var _this = this;

      nodes.forEach(function (node, index) {
        var newPath = path.concat(index);
        var annotationKeys = _this.nodeToAnnotationsMap.get(node);

        if (annotationKeys) {
          annotationKeys.forEach(function (key) {
            annotations = annotations.set(key, _this.expandAnnotationRange(annotations.get(key), newPath, node.text.length));
          });
        }

        if (node.nodes) {
          annotations = _this.updateAnnotationPaths(node.nodes, newPath, annotations);
        }
      });

      return annotations;
    }

    /**
     * Expands an annotation's range to overlap the node at a `path`.
     *
     * @param {Object} annotation
     * @param {List} path
     * @param {Number} length
     * @return {Object}
     */

  }, {
    key: 'expandAnnotationRange',
    value: function expandAnnotationRange(annotation, path, length) {
      if (!annotation.anchor.path || PathUtils.compare(path, annotation.anchor.path) < 0) {
        annotation = annotation.setAnchor(Point.create({
          path: path,
          offset: 0
        }));
      }

      if (!annotation.focus.path || PathUtils.compare(path, annotation.focus.path) > 0 || PathUtils.compare(path, annotation.focus.path) === 0 && length > annotation.focus.offset) {
        annotation = annotation.setFocus(Point.create({
          path: path,
          offset: length
        }));
      }

      return annotation;
    }

    /**
     * Deserialize an array of DOM elements.
     *
     * @param {Array} elements
     * @return {Array}
     */

    /**
     * Deserialize a DOM element.
     *
     * @param {Object} element
     * @return {Any}
     */

    /**
     * Deserialize a `mark` object.
     *
     * @param {Object} mark
     * @return {Array}
     */

    /**
     * Deserialize an `annotation` object.
     *
     * @param {Object} annotation
     * @return {Array}
     */

    /**
     * Serialize a `value` object into an HTML string.
     *
     * @param {Value} value
     * @param {Object} options
     *   @property {Boolean} render
     * @return {String|Array}
     */

    /**
     * Serialize a `node`.
     *
     * @param {Node} node
     * @return {String}
     */

    /**
     * Serialize a `string`.
     *
     * @param {String} string
     * @return {String}
     */

    /**
     * Filter out cruft newline nodes inserted by the DOM parser.
     *
     * @param {Object} element
     * @return {Boolean}
     */

  }]);
  return Html;
}();

/**
 * Add a unique key to a React `element`.
 *
 * @param {Element} element
 * @return {Element}
 */

var _initialiseProps = function _initialiseProps() {
  var _this2 = this;

  this.deserialize = function (html) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var _options$toJSON = options.toJSON,
        toJSON = _options$toJSON === undefined ? false : _options$toJSON;
    var defaultBlock = _this2.defaultBlock,
        parseHtml = _this2.parseHtml;

    var fragment = parseHtml(html);
    var children = Array.from(fragment.childNodes);
    var nodes = _this2.deserializeElements(children);

    // COMPAT: ensure that all top-level inline nodes are wrapped into a block.
    nodes = nodes.reduce(function (memo, node, i, original) {
      if (node.object === 'block') {
        memo.push(node);
        return memo;
      }

      if (i > 0 && original[i - 1].object !== 'block') {
        var _block = memo[memo.length - 1];
        _block.nodes.push(node);
        return memo;
      }

      var block = _extends({
        object: 'block',
        data: {}
      }, defaultBlock, {
        nodes: [node]
      });

      memo.push(block);
      return memo;
    }, []);

    // TODO: pretty sure this is no longer needed.
    if (nodes.length === 0) {
      nodes = [_extends({
        object: 'block',
        data: {}
      }, defaultBlock, {
        nodes: [{
          object: 'text',
          text: '',
          marks: []
        }]
      })];
    }

    var json = {
      object: 'value',
      document: {
        object: 'document',
        data: {},
        nodes: nodes
      }
    };

    _this2.annotations = _this2.updateAnnotationPaths(nodes, List(), _this2.annotations);

    if (!_this2.annotations.isEmpty()) {
      json.annotations = _this2.annotations.toJSON();
    }

    _this2.annotations = _this2.annotations.clear();
    _this2.nodeToAnnotationsMap = _this2.nodeToAnnotationsMap.clear();

    var ret = toJSON ? json : Value.fromJSON(json);
    return ret;
  };

  this.deserializeElements = function () {
    var elements = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];

    var nodes = [];

    elements.filter(_this2.cruftNewline).forEach(function (element) {
      var node = _this2.deserializeElement(element);

      switch (typeOf(node)) {
        case 'array':
          nodes = nodes.concat(node);
          break;
        case 'object':
          nodes.push(node);
          break;
      }
    });

    return nodes;
  };

  this.deserializeElement = function (element) {
    var node = void 0;

    if (!element.tagName) {
      element.tagName = '';
    }

    var next = function next(elements) {
      if (Object.prototype.toString.call(elements) === '[object NodeList]') {
        elements = Array.from(elements);
      }

      switch (typeOf(elements)) {
        case 'array':
          return _this2.deserializeElements(elements);
        case 'object':
          return _this2.deserializeElement(elements);
        case 'null':
        case 'undefined':
          return;
        default:
          throw new Error('The `next` argument was called with invalid children: "' + elements + '".');
      }
    };

    var _iteratorNormalCompletion = true;
    var _didIteratorError = false;
    var _iteratorError = undefined;

    try {
      for (var _iterator = _this2.rules[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
        var rule = _step.value;

        if (!rule.deserialize) continue;
        var ret = rule.deserialize(element, next);
        var type = typeOf(ret);

        if (type !== 'array' && type !== 'object' && type !== 'null' && type !== 'undefined') {
          throw new Error('A rule returned an invalid deserialized representation: "' + node + '".');
        }

        if (ret === undefined) {
          continue;
        } else if (ret === null) {
          return null;
        } else if (ret.object === 'mark') {
          node = _this2.deserializeMark(ret);
        } else if (ret.object === 'annotation') {
          node = _this2.deserializeAnnotation(ret);
        } else {
          node = ret;
        }

        if (node.object === 'block' || node.object === 'inline') {
          node.data = node.data || {};
          node.nodes = node.nodes || [];
        } else if (node.object === 'text') {
          node.marks = node.marks || [];
          node.text = node.text || '';
        }

        break;
      }
    } catch (err) {
      _didIteratorError = true;
      _iteratorError = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion && _iterator.return) {
          _iterator.return();
        }
      } finally {
        if (_didIteratorError) {
          throw _iteratorError;
        }
      }
    }

    return node || next(element.childNodes);
  };

  this.deserializeMark = function (mark) {
    var type = mark.type,
        data = mark.data;


    var applyMark = function applyMark(node) {
      if (node.object === 'mark') {
        var ret = _this2.deserializeMark(node);
        return ret;
      } else if (node.object === 'text') {
        node.marks = node.marks || [];
        node.marks.push({ type: type, data: data });
      } else if (node.nodes) {
        node.nodes = node.nodes.map(applyMark);
      }

      return node;
    };

    return mark.nodes.reduce(function (nodes, node) {
      var ret = applyMark(node);
      if (Array.isArray(ret)) return nodes.concat(ret);
      nodes.push(ret);
      return nodes;
    }, []);
  };

  this.deserializeAnnotation = function (annotation) {
    var addAnnotation = function addAnnotation(node) {
      if (node.object === 'annotation') {
        var ret = _this2.deserializeAnnotation(node);
        return ret;
      } else if (node.object === 'text') {
        if (!_this2.annotations.get(annotation.key)) {
          delete annotation.nodes;

          _this2.annotations = _this2.annotations.set(annotation.key, Annotation.create(annotation));
        }

        _this2.nodeToAnnotationsMap = _this2.nodeToAnnotationsMap.set(node, (_this2.nodeToAnnotationsMap.get(node) || List()).concat(annotation.key));
      } else if (node.nodes) {
        node.nodes = node.nodes.map(addAnnotation);
      }

      return node;
    };

    return annotation.nodes.reduce(function (nodes, node) {
      var ret = addAnnotation(node);
      if (Array.isArray(ret)) return nodes.concat(ret);
      nodes.push(ret);
      return nodes;
    }, []);
  };

  this.serialize = function (value) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var document = value.document,
        annotations = value.annotations;

    var elements = document.nodes.map(function (n, index) {
      var nodeAnnotations = annotations.map(function (a) {
        return a.relativeToChild(document, index);
      }).filter(function (a) {
        return a;
      });

      return _this2.serializeNode(n, nodeAnnotations);
    }).filter(function (el) {
      return el;
    });
    if (options.render === false) return elements;

    var html = renderToStaticMarkup(React.createElement(
      'body',
      null,
      elements
    ));
    var inner = html.slice(6, -7);
    return inner;
  };

  this.serializeNode = function (node, annotations) {
    if (node.object === 'text') {
      var leaves = node.getLeaves(annotations, []);
      return leaves.map(function (leaf) {
        return _this2.serializeLeaf(leaf);
      });
    }

    var children = node.nodes.reduce(function (acc, child, index) {
      var nodeAnnotations = annotations.map(function (a) {
        return a.relativeToChild(node, index);
      }).filter(function (a) {
        return a;
      });

      return acc.concat(_this2.serializeNode(child, nodeAnnotations));
    }, []);

    var _iteratorNormalCompletion2 = true;
    var _didIteratorError2 = false;
    var _iteratorError2 = undefined;

    try {
      for (var _iterator2 = _this2.rules[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
        var rule = _step2.value;

        if (!rule.serialize) continue;
        var ret = rule.serialize(node, children);
        if (ret === null) return;
        if (ret) return addKey(ret);
      }
    } catch (err) {
      _didIteratorError2 = true;
      _iteratorError2 = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion2 && _iterator2.return) {
          _iterator2.return();
        }
      } finally {
        if (_didIteratorError2) {
          throw _iteratorError2;
        }
      }
    }

    throw new Error('No serializer defined for node of type "' + node.type + '".');
  };

  this.serializeLeaf = function (leaf) {
    var string = new String({ text: leaf.text });
    var text = _this2.serializeString(string);

    return leaf.annotations.concat(leaf.marks).reduce(function (children, mark) {
      var _iteratorNormalCompletion3 = true;
      var _didIteratorError3 = false;
      var _iteratorError3 = undefined;

      try {
        for (var _iterator3 = _this2.rules[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
          var rule = _step3.value;

          if (!rule.serialize) continue;
          var ret = rule.serialize(mark, children);
          if (ret === null) return;
          if (ret) return addKey(ret);
        }
      } catch (err) {
        _didIteratorError3 = true;
        _iteratorError3 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion3 && _iterator3.return) {
            _iterator3.return();
          }
        } finally {
          if (_didIteratorError3) {
            throw _iteratorError3;
          }
        }
      }

      throw new Error('No serializer defined for ' + mark.object + ' of type "' + mark.type + '".');
    }, text);
  };

  this.serializeString = function (string) {
    var _iteratorNormalCompletion4 = true;
    var _didIteratorError4 = false;
    var _iteratorError4 = undefined;

    try {
      for (var _iterator4 = _this2.rules[Symbol.iterator](), _step4; !(_iteratorNormalCompletion4 = (_step4 = _iterator4.next()).done); _iteratorNormalCompletion4 = true) {
        var rule = _step4.value;

        if (!rule.serialize) continue;
        var ret = rule.serialize(string, string.text);
        if (ret) return ret;
      }
    } catch (err) {
      _didIteratorError4 = true;
      _iteratorError4 = err;
    } finally {
      try {
        if (!_iteratorNormalCompletion4 && _iterator4.return) {
          _iterator4.return();
        }
      } finally {
        if (_didIteratorError4) {
          throw _iteratorError4;
        }
      }
    }
  };

  this.cruftNewline = function (element) {
    return !(element.nodeName === '#text' && element.nodeValue === '\n');
  };
};

var key = 0;

function addKey(element) {
  return React.cloneElement(element, { key: key++ });
}

export default Html;
//# sourceMappingURL=slate-html-serializer.es.js.map
